import { useState } from "react";
import { Tooltip } from "@heroui/react";
import { useQueries } from "@tanstack/react-query";
import { FiCheckCircle } from "react-icons/fi";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import type { DuplicateOrder, OrderStatus } from "./types";

interface DuplicateIndicatorProps {
  duplicates: DuplicateOrder[];
  isActive?: boolean;
  t: (key: string) => string;
}

interface DuplicateWithStatus extends DuplicateOrder {
  resolvedStatus?: OrderStatus;
}

export function DuplicateIndicator({
  duplicates,
  isActive = false,
  t,
}: Readonly<DuplicateIndicatorProps>) {
  const axiosPrivate = useAxiosPrivate();
  const [isHovered, setIsHovered] = useState(false);

  const statusQueries = useQueries({
    queries: duplicates.map((dup) => ({
      queryKey: ["orderStatus", dup.jobID],
      queryFn: async (): Promise<OrderStatus> => {
        const response = await axiosPrivate.get(`/orders/${dup.jobID}`);
        return response.data.process.status;
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 3 * 60 * 1000,
      enabled: isHovered,
      retry: 1,
    })),
  });

  const duplicatesWithStatus: DuplicateWithStatus[] = duplicates.map((dup, index) => ({
    ...dup,
    resolvedStatus: statusQueries[index]?.data,
  }));

  const renderDuplicateContent = (dupOrder: DuplicateWithStatus) => {
    const isSuccess = dupOrder.resolvedStatus === "success";
    const textColorClass = isSuccess ? "" : "text-vistactaorange";

    return (
      <span className={`font-medium flex items-center gap-1.5 max-w-full ${textColorClass}`}>
        {isSuccess && (
          <FiCheckCircle className="text-vistagreen flex-shrink-0 text-base" />
        )}
        <span className="truncate min-w-0">
          {new Date(dupOrder.start_datetime).toLocaleDateString()} - {dupOrder.subject}
        </span>
      </span>
    );
  };

  return (
    <Tooltip
      content={
        <div className="px-3 py-2 w-72">
          <div className="text-small font-bold mb-1">{t("duplicate_hash_warning")}</div>
          <div className="text-tiny flex flex-col gap-1 overflow-hidden">
            {duplicatesWithStatus.map((dupOrder) =>
              isActive ? (
                <span key={dupOrder.jobID} className="text-default-400 flex flex-col">
                  {renderDuplicateContent(dupOrder)}
                </span>
              ) : (
                <a
                  key={dupOrder.jobID}
                  href={`/orders/${dupOrder.jobID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline flex flex-col"
                >
                  {renderDuplicateContent(dupOrder)}
                </a>
              )
            )}
          </div>
        </div>
      }
      placement="bottom"
      className="max-w-xs"
    >
      <button
        type="button"
        className="ml-1 text-warning text-tiny bg-warning-100 rounded-full px-2 py-0.5 flex items-center whitespace-nowrap border-none"
        onMouseEnter={() => setIsHovered(true)}
        onFocus={() => setIsHovered(true)}
      >
        D
      </button>
    </Tooltip>
  );
}

export default DuplicateIndicator;
